import { format, isValid, parseISO } from 'date-fns';
import { isTerminal } from '../../../utils/orderStatusLabel';

export const DEFAULT_FILTERS = Object.freeze({
  scope: 'open',
  statuses: [],
  time: 'all',
  from: '',
  to: '',
  query: '',
});

const toDate = (iso) => (iso ? new Date(iso) : null);

/** 'yyyy-MM-dd' for an ISO string or Date (the provider's availabilityDates arrive in either form), else null. */
export function toDateStr(dateInput) {
  if (!dateInput) return null;
  const d = typeof dateInput === 'string' ? parseISO(dateInput) : new Date(dateInput);
  return isValid(d) ? format(d, 'yyyy-MM-dd') : null;
}

/** The date an order is "about": the booked start for SERVICE, the purchase for STORE. */
export function primaryDate(order) {
  if (!order) return null;
  const service = (order.orderType || 'SERVICE') === 'SERVICE';
  return toDate((service && order.serviceStartAt) || order.createdAt);
}

/**
 * When the order stops being "upcoming": the expected end, else the start. A SERVICE order with no
 * date yet (an inquiry awaiting a proposal) is open work, so it has no end and stays upcoming;
 * a STORE order ages from its purchase.
 */
export function endDate(order) {
  if (!order) return null;
  if (order.expectedCompletionAt || order.serviceStartAt) return toDate(order.expectedCompletionAt || order.serviceStartAt);
  return order.orderType === 'STORE' ? toDate(order.createdAt) : null;
}

export function isUpcoming(order, now = new Date()) {
  const end = endDate(order);
  return !end || end.getTime() >= now.getTime();
}

/** A SERVICE order that has no booked start yet: an inquiry or a proposal-less thread. */
export const isUndated = (order) =>
  (order?.orderType || 'SERVICE') === 'SERVICE' && !order?.serviceStartAt && !order?.serviceStartLocal;

const CANCELLED_STATUSES = ['CANCELLED_BY_USER', 'CANCELLED_BY_PROVIDER', 'CANCELLED_PAYMENT_FAILED'];

/**
 * "Open" scope: is there still work to do on this order? For a SERVICE booking the business
 * status decides. For a STORE order the booking status reaches COMPLETED at capture, long before
 * the goods move, so the fulfilment block decides: cancelled or delivered is closed, anything
 * else (awaiting shipment, shipped) is still the store's job. Orders paid before fulfilment
 * tracking existed have no block and fall back to the business status.
 */
export function isClosed(order) {
  if (order?.orderType === 'STORE') {
    if (CANCELLED_STATUSES.includes(order.status)) return true;
    if (order.fulfilment?.status) return order.fulfilment.status === 'DELIVERED';
  }
  return isTerminal(order);
}

/**
 * Lower-cased haystack for the search box. `notesIndex` is { [orderId]: { latestBody, searchText } };
 * `searchText` carries every note of the order (server-capped), `latestBody` is the fallback.
 */
export function searchText(order, notesIndex = {}) {
  const note = notesIndex[order.id];
  const parts = [
    `#${order.id ?? ''}`,
    order.user?.name,
    ...(order.dogs || []).map((d) => d?.name),
    order.hasProposal || order.orderType === 'STORE' ? order.dogServiceType : null,
    order.providerDogService?.title,
    ...(order.orderItems || []).map((i) => i?.storeProduct?.name),
    note?.searchText || note?.latestBody,
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

const dayStart = (ymd) => (ymd ? new Date(`${ymd}T00:00:00`) : null);
const dayEnd = (ymd) => (ymd ? new Date(`${ymd}T23:59:59.999`) : null);

export function applyFilters(orders, filters, notesIndex = {}, now = new Date()) {
  const f = { ...DEFAULT_FILTERS, ...filters };
  const from = dayStart(f.from);
  const to = dayEnd(f.to);
  const query = f.query.trim().toLowerCase();
  return orders.filter((order) => {
    if (f.scope === 'open' && isClosed(order)) return false;
    if (f.statuses.length > 0 && !f.statuses.includes(order.status)) return false;
    if (f.time === 'upcoming' && !isUpcoming(order, now)) return false;
    if (f.time === 'past' && isUpcoming(order, now)) return false;
    if (f.time === 'undated' && !isUndated(order)) return false;
    if (from || to) {
      const d = primaryDate(order);
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
    }
    if (query && !searchText(order, notesIndex).includes(query)) return false;
    return true;
  });
}
